import moment from "moment";
import isPointInPolygon from "@turf/boolean-point-in-polygon";
import mergewith from "lodash.mergewith";

export const getMapParams = (module, store) => {
  const { settings } = store.state;
  const DEFAULT_CENTER = settings
    ? settings.map_initial_central_point
    : [37.6165, 55.752];
  const zoom = localStorage.getItem(`${module}Zoom`)
    ? JSON.parse(localStorage.getItem(`${module}Zoom`))
    : 10;
  let center = localStorage.getItem(`${module}Center`)
    ? JSON.parse(localStorage.getItem(`${module}Center`))
    : DEFAULT_CENTER;

  if (center[0] > 180 || center[1] > 180) center = DEFAULT_CENTER;

  return { center, zoom };
};

export const customizer = (objValue, srcValue) => {
  if (Array.isArray(objValue)) {
    const result = objValue.concat(srcValue);
    return result.filter((el, i) => result.indexOf(el) === i);
  }
};

const getBBox = map => {
  const { mapgl } = map;
  const bounds = mapgl.getBounds();
  const { _ne, _sw } = bounds;

  return [
    [_sw.lng, _ne.lat],
    [_ne.lng, _ne.lat],
    [_ne.lng, _sw.lat],
    [_sw.lng, _sw.lat]
  ];
};

export const getFeaturesInBboxLength = (features = [], map) => {
  const bbox = getBBox(map);
  const bboxFeature = {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [[...bbox, bbox[0]]]
    }
  };
  const featuresInBbox = features.filter(f => isPointInPolygon(f, bboxFeature));

  return featuresInBbox.length;
};

export const getRequestConfig = (map, id, geom_type) => {
  const fields = map.requestedFields[id];
  const initial = {
    only: [],
    include: {}
  };
  fields.forEach(f => {
    if (map.objectFieldsConfigs[id] && map.objectFieldsConfigs[id][f])
      mergewith(initial, map.objectFieldsConfigs[id][f], customizer);
    else mergewith(initial, { only: [f] }, customizer);
  });
  const config = {
    limit: geom_type === "point" ? 50000 : 10000,
    where: [
      {
        field: "geom",
        op: "!null",
        type: "AND",
        value: ""
      }
    ],
    ...initial
  };

  // bbox settings
  const bboxSettings = {
    field: "geom",
    op: "bbox",
    type: "AND",
    value: getBBox(map)
  };

  if (geom_type && geom_type !== "point") {
    config.where.push(bboxSettings);
  }

  // timemachine filters
  const {
    enabled,
    range,
    point,
    attribute,
    type,
    format,
    isNested,
    layerId,
    sourceName
  } = map.timemachineState;
  if (enabled) {
    const includeConfig = config.include[sourceName];

    if (isNested && layerId === id && includeConfig) {
      if (type === "interval") {
        includeConfig.where = [
          {
            field: attribute,
            op: "between",
            type: "AND",
            value: `${range[0]}/${range[1]}`,
            date_trunc: format
          }
        ];
      } else if (type === "point") {
        includeConfig.where = [
          {
            field: attribute,
            op: "=",
            type: "AND",
            value: point,
            date_trunc: format
          }
        ];
      }
    } else {
      const field = map.objectFields[id].find(f => f.title === attribute);
      if (field) {
        const from = moment(range[0]).format();
        const to = moment(range[1]).format();
        const value = moment(point).format();
        if (type === "interval") {
          config.where.push({
            field: attribute,
            op: "between",
            type: "AND",
            value: `${from}/${to}`,
            date_trunc: format
          });
        } else if (type === "point") {
          config.where.push({
            field: attribute,
            op: "=",
            type: "AND",
            value,
            date_trunc: format
          });
        }
      }
    }
  }
  // local filters
  const layerFilters = map.styleConfig[id].filters;
  if (layerFilters.length) {
    layerFilters
      .filter(f => f.field && !!f.enabled && f.id !== map.timemachineState.id)
      .forEach(f => {
        const field =
          map.objectFields[id].find(field => field.title === f.field) || {};
        const { type } = field;

        if (type === "string" || type === "boolean") {
          config.where.push({
            field: f.field,
            op: f.operation,
            type: "AND",
            value: f.values
          });
        } else {
          let [from, to] = f.range;
          if (type === "datetime") {
            from = moment(from).format();
            to = moment(to).format();
          }
          config.where.push({
            field: f.field,
            op: "between",
            type: "AND",
            value: `${from}/${to}`
          });
        }
      });
  }

  // sorting
  const { order } = map.styleConfig[id];
  if (order.field) {
    config.order = [{ [order.field]: order.type }];
  }

  return config;
};

const onlyConfig = { only: ["id", "geom", "no"] };
const getIncludeConfig = geom_type => {
  switch (geom_type) {
    case "nodes":
      return {
        include: {
          from_links: onlyConfig,
          to_links: onlyConfig,
          connectors: { only: ["id", "geom"] },
          stop_points: onlyConfig
        }
      };
    case "links":
      return {
        include: {
          stop_point: onlyConfig
        }
      };
    case "zones":
      return {
        include: {
          connectors: { only: ["id", "geom"] }
        }
      };
    case "stop_points":
      return {
        only: ["id", { field: "stop_geom", func: "st_as_geojson" }, "geom"]
      };
    case "line_routes":
      return {
        only: [
          "id",
          "geom",
          "transport_system_id",
          "reverse_route_id",
          "rev",
          "name"
        ],
        include: {
          line_route_items: {
            only: ["node_id", "stop_point_id", "order_no"],
            include: {
              node: {
                only: ["id", "no", "geom"]
              },
              stop_point: {
                only: ["id", "no", "geom"]
              }
            },
            where: [
              {
                field: "node_id",
                op: "!null",
                type: "OR",
                value: ""
              },
              {
                field: "stop_point_id",
                op: "!null",
                type: "OR",
                value: ""
              }
            ]
          }
        }
      };
    case "events":
      return {
        only: undefined
      };
  }
};

export const getEditorRequestConfig = (map, geom_type) => {
  return {
    limit: map.getObjectsLimit(geom_type),
    only: ["id", "geom"],
    where: [
      {
        field: "geom",
        op: "bbox",
        type: "AND",
        value: getBBox(map)
      },
      {
        field: "geom",
        op: "!null",
        type: "AND",
        value: ""
      }
    ],
    ...getIncludeConfig(geom_type)
  };
};

export const flyToOptions = {
  speed: 10,
  curve: 1,
  easing(t) {
    return t;
  }
};

export const getCalculationsRequestConfig = (component, id, datatype) => {
  const { $store, $route } = component;
  const {
    requestedFields,
    objectFields,
    currentTime
  } = $store.state.calculations;
  const fields = objectFields[id];
  const only = requestedFields[id].map(name => {
    const fieldConfig = fields.find(field => field.title === name);

    if (fieldConfig) {
      const { calc_field } = fieldConfig;

      return calc_field ? `link_calculation_results.${name}` : name;
    }

    return name;
  });
  const config = {
    limit: 10000,
    where: [
      {
        field: "geom",
        op: "bbox",
        type: "AND",
        value: getBBox(component)
      },
      {
        field: "geom",
        op: "!null",
        type: "AND",
        value: ""
      }
    ],
    ...getIncludeConfig(datatype),
    only
  };

  if ($route.name === "c-scenario" && datatype === "links") {
    config.where.push({
      field: "link_calculation_results.calculation_id",
      op: "=",
      type: "AND",
      value: $route.params.scenarioId
    });

    if (currentTime !== null) {
      config.where.push({
        field: "link_calculation_results.time_value",
        op: "=",
        type: "AND",
        value: currentTime
      });
    }
  }

  return config;
};
